import { api } from "@/lib/apiClient";
import { getStoredAuthUser } from "@/lib/authUser";
import { isAxiosError } from "axios";

export type SymbolPosition = "left" | "right" | "left_with_space" | "right_with_space";

export interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  symbolPosition: SymbolPosition;
  decimalPlaces: number;
  decimalSeparator: string;
  thousandSeparator: string;
  isActive: boolean;
}

export type CurrencyDraft = {
  code: string;
  name: string;
  symbol: string;
  symbolPosition: SymbolPosition;
  decimalPlaces: number;
  decimalSeparator: string;
  thousandSeparator: string;
  isActive: boolean;
};

export const DEFAULT_PKR_CURRENCY: Currency = {
  id: 1,
  code: "PKR",
  name: "Pakistani Rupee",
  symbol: "Rs.",
  symbolPosition: "left_with_space",
  decimalPlaces: 2,
  decimalSeparator: ".",
  thousandSeparator: ",",
  isActive: true,
};

export type ApiCurrency = {
  id: number;
  code: string;
  name: string;
  symbol: string;
  symbol_position: SymbolPosition;
  decimal_places: number;
  decimal_separator: string;
  thousand_separator: string;
  is_active: boolean;
};

export function mapApiCurrency(item: ApiCurrency): Currency {
  return {
    id: Number(item.id),
    code: item.code,
    name: item.name,
    symbol: item.symbol,
    symbolPosition: item.symbol_position || "left_with_space",
    decimalPlaces: item.decimal_places ?? 2,
    decimalSeparator: item.decimal_separator || ".",
    thousandSeparator: item.thousand_separator || ",",
    isActive: Boolean(item.is_active),
  };
}

export const DEFAULT_CURRENCIES: Currency[] = [
  DEFAULT_PKR_CURRENCY,
  { id: 2, code: "USD", name: "US Dollar", symbol: "$", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 3, code: "EUR", name: "Euro", symbol: "€", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 4, code: "GBP", name: "British Pound", symbol: "£", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 5, code: "AED", name: "UAE Dirham", symbol: "AED", symbolPosition: "left_with_space", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 6, code: "SAR", name: "Saudi Riyal", symbol: "SAR", symbolPosition: "left_with_space", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
  { id: 7, code: "CAD", name: "Canadian Dollar", symbol: "CA$", symbolPosition: "left", decimalPlaces: 2, decimalSeparator: ".", thousandSeparator: ",", isActive: true },
];

let cachedCurrencies: Currency[] = DEFAULT_CURRENCIES;

export async function fetchCurrencies(): Promise<Currency[]> {
  try {
    const response = await api.get<{ data: ApiCurrency[] }>("/api/v1/currencies/options");
    const rows = response.data?.data;
    if (Array.isArray(rows) && rows.length > 0) {
      cachedCurrencies = rows.map(mapApiCurrency);
    }
  } catch {
    try {
      const response = await api.get<{ data: ApiCurrency[] }>("/api/v1/currencies");
      const rows = response.data?.data;
      if (Array.isArray(rows) && rows.length > 0) {
        cachedCurrencies = rows.map(mapApiCurrency);
      }
    } catch (error) {
      console.error("Failed to fetch currencies from API", error);
    }
  }
  return cachedCurrencies;
}

export type ListCurrenciesParams = {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  is_active?: boolean | null;
};

export type CurrenciesPaginatedResponse = {
  data: Currency[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export async function listCurrencies(params: ListCurrenciesParams = {}): Promise<CurrenciesPaginatedResponse> {
  const query: Record<string, string | number | boolean> = {};
  if (params.page) query.page = params.page;
  if (params.per_page) query.per_page = params.per_page;
  if (params.search) query.search = params.search;
  if (params.sort) query.sort = params.sort;
  if (typeof params.is_active === "boolean") query.is_active = params.is_active;

  const response = await api.get<{ data: ApiCurrency[]; meta: CurrenciesPaginatedResponse["meta"] }>("/api/v1/currencies", { params: query });
  const rows = (response.data.data ?? []).map(mapApiCurrency);
  return {
    data: rows,
    meta: response.data.meta ?? { current_page: 1, last_page: 1, per_page: rows.length, total: rows.length },
  };
}

export async function createCurrency(draft: CurrencyDraft): Promise<Currency> {
  const payload = {
    code: draft.code,
    name: draft.name,
    symbol: draft.symbol,
    symbol_position: draft.symbolPosition,
    decimal_places: draft.decimalPlaces,
    decimal_separator: draft.decimalSeparator,
    thousand_separator: draft.thousandSeparator,
    is_active: draft.isActive,
  };
  const response = await api.post<{ data: ApiCurrency }>("/api/v1/currencies", payload);
  return mapApiCurrency(response.data.data);
}

export async function updateCurrency(id: number | string, draft: Partial<CurrencyDraft>): Promise<Currency> {
  const payload: Record<string, unknown> = {};
  if (draft.code !== undefined) payload.code = draft.code;
  if (draft.name !== undefined) payload.name = draft.name;
  if (draft.symbol !== undefined) payload.symbol = draft.symbol;
  if (draft.symbolPosition !== undefined) payload.symbol_position = draft.symbolPosition;
  if (draft.decimalPlaces !== undefined) payload.decimal_places = draft.decimalPlaces;
  if (draft.decimalSeparator !== undefined) payload.decimal_separator = draft.decimalSeparator;
  if (draft.thousandSeparator !== undefined) payload.thousand_separator = draft.thousandSeparator;
  if (draft.isActive !== undefined) payload.is_active = draft.isActive;

  const response = await api.put<{ data: ApiCurrency }>(`/api/v1/currencies/${id}`, payload);
  return mapApiCurrency(response.data.data);
}

export async function deleteCurrency(id: number | string): Promise<void> {
  await api.delete(`/api/v1/currencies/${id}`);
}

export function currencyApiErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.data?.message) {
    const data = error.response.data as { message?: string; errors?: Record<string, string[]> };
    if (data.errors) {
      const firstKey = Object.keys(data.errors)[0];
      if (firstKey && data.errors[firstKey]?.[0]) {
        return data.errors[firstKey][0];
      }
    }
    return data.message;
  }
  return "An unexpected error occurred.";
}

export function getCachedCurrencies(): Currency[] {
  return cachedCurrencies;
}

export function getActiveCompanyCurrency(): Currency {
  const user = getStoredAuthUser();
  const c = user?.company_branding?.currency;
  if (c && typeof c === "object" && c.symbol) {
    return c;
  }
  return DEFAULT_PKR_CURRENCY;
}

/**
 * Global Currency Formatting Engine
 * Respects company currency symbol, position (left/right/with_space), and decimal precision.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyOverride?: Partial<Currency> | string | null,
): string {
  if (amount == null || !Number.isFinite(amount)) {
    return "—";
  }

  let currencyConfig: Currency = getActiveCompanyCurrency();

  if (typeof currencyOverride === "string" && currencyOverride.trim()) {
    const overrideCode = currencyOverride.trim().toUpperCase();
    if (overrideCode !== "PKR" || currencyConfig.code.toUpperCase() === "PKR") {
      const found = cachedCurrencies.find((c) => c.code.toUpperCase() === overrideCode);
      if (found) {
        currencyConfig = found;
      } else {
        currencyConfig = { ...currencyConfig, symbol: currencyOverride.trim(), code: currencyOverride.trim() };
      }
    }
  } else if (currencyOverride && typeof currencyOverride === "object") {
    currencyConfig = { ...currencyConfig, ...currencyOverride };
  }

  const { symbol, symbolPosition, decimalPlaces, decimalSeparator, thousandSeparator } = currencyConfig;

  const num = Math.abs(amount);
  const fixedStr = num.toFixed(decimalPlaces);
  const [integerPart, decimalPart] = fixedStr.split(".");

  // Apply thousand separator
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator || ",");
  const numberFormatted = decimalPlaces > 0 && decimalPart ? `${formattedInteger}${decimalSeparator || "."}${decimalPart}` : formattedInteger;
  const signedNumber = amount < 0 ? `-${numberFormatted}` : numberFormatted;

  switch (symbolPosition) {
    case "left":
      return `${symbol}${signedNumber}`;
    case "right":
      return `${signedNumber}${symbol}`;
    case "right_with_space":
      return `${signedNumber} ${symbol}`;
    case "left_with_space":
    default:
      return `${symbol} ${signedNumber}`;
  }
}
